import { apiRequest } from '../api/client';

export type OrderStatus = 'DRAFT' | 'SUBMITTED' | 'CONFIRMED' | 'CANCELLED';

/** Matches `apps/api` `OrderItemPositionSummary` (`orders/domain/order.repository.ts`). */
export interface OrderItemPositionSummary {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
}

export interface OrderItem {
  id: string;
  menuId: string;
  menuNameSnapshot: string;
  /** `null` only for items frozen before `rotating-menu` (`design.md` §1.4, T6). */
  positionId: string | null;
  position: OrderItemPositionSummary | null;
  quantity: number;
  notes: string | null;
  dietaryTags: string[];
  allergens: string[];
}

export interface OrderChangeRequest {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'APPLIED';
  /** The full desired line list the client asked for (`portal-order.mapper.ts`). */
  requestedChanges?: { items: { positionId: string; quantity: number; notes?: string | null }[] };
  rejectionReason: string | null;
  appliedAt: string | null;
  createdAt: string;
}

/**
 * Mirrors `apps/api/src/modules/portal/application/portal-order.mapper.ts`'s
 * `PortalOrderResponse` (`client-portal/design.md` §0.12: no shared
 * package, DTOs are hand-mirrored per app). No `unitPriceCents`/
 * `subtotalCents`/`vatCents`/`totalCents` field exists here — the
 * backend never sends them, so there is nothing to hide.
 */
export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  siteId: string;
  siteName: string;
  /** Absent on orders predating points of sale; the history shows the unit only with several. */
  operatingUnitId?: string | null;
  operatingUnitName?: string | null;
  serviceDate: string;
  status: OrderStatus;
  source: 'BACKOFFICE' | 'CLIENT_PORTAL' | 'TEMPLATE';
  templateId: string | null;
  totalQuantity: number;
  /** ISO 8601 with offset, computed by the server — never recomputed client-side. */
  cutoffAt: string;
  cutoffOverridden: boolean;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  clientNotes: string | null;
  items: OrderItem[];
  changeRequests: OrderChangeRequest[];
  createdAt: string;
  updatedAt: string;
}

export interface AvailableMenuServiceType {
  code: string;
  name: string;
  displayOrder: number;
}

/** Matches `apps/api` `MenuCalendarPositionSummary` (`calendar/domain/menu-calendar.repository.ts`). */
export interface AvailableMenuPosition {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  serviceTypeId: string;
}

export interface AvailableMenu {
  menuId: string;
  positionId: string;
  position: AvailableMenuPosition;
  name: string;
  description: string | null;
  serviceType: AvailableMenuServiceType;
  dietaryTags: string[];
  allergens: string[];
  maxQuantity: number | null;
  reservedQuantity: number;
  availableQuantity: number | null;
}

export interface OrderListResult {
  items: Order[];
  page: number;
  pageSize: number;
  total: number;
}

export interface OrderListFilter {
  serviceDate?: string;
  /** Inclusive range bounds, calendar days. Independent of `serviceDate`. */
  dateFrom?: string;
  dateTo?: string;
  siteId?: string;
  operatingUnitId?: string;
  status?: OrderStatus;
  page?: number;
  pageSize?: number;
}

/** `rotating-menu/design.md` §3.4: one line per position — the server resolves the dish published in that position that day. */
export interface OrderLineInput {
  positionId: string;
  quantity: number;
  notes?: string | null;
}

export interface CreateOrderInput {
  siteId: string;
  /** Sent only when the customer has several units; the server infers it otherwise. */
  operatingUnitId?: string;
  serviceDate: string;
  items: OrderLineInput[];
  clientNotes?: string | null;
}

export interface UpdateOrderInput {
  items: OrderLineInput[];
  clientNotes?: string | null;
}

export interface RepeatOrderResult {
  data: Order | null;
  error?: 'ORDER_REPEAT_NO_PREVIOUS_ORDER';
}

function toQueryString(filter: OrderListFilter): string {
  const params = new URLSearchParams();

  if (filter.serviceDate !== undefined) params.set('serviceDate', filter.serviceDate);
  if (filter.dateFrom !== undefined) params.set('dateFrom', filter.dateFrom);
  if (filter.dateTo !== undefined) params.set('dateTo', filter.dateTo);
  if (filter.siteId !== undefined) params.set('siteId', filter.siteId);
  if (filter.operatingUnitId !== undefined) params.set('operatingUnitId', filter.operatingUnitId);
  if (filter.status !== undefined) params.set('status', filter.status);
  if (filter.page !== undefined) params.set('page', String(filter.page));
  if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));

  const query = params.toString();

  return query === '' ? '' : `?${query}`;
}

export function listOrders(filter: OrderListFilter = {}): Promise<OrderListResult> {
  return apiRequest<OrderListResult>(`/portal/orders${toQueryString(filter)}`);
}

export function getOrder(orderId: string): Promise<Order> {
  return apiRequest<Order>(`/portal/orders/${orderId}`);
}

export function createOrder(input: CreateOrderInput): Promise<Order> {
  return apiRequest<Order>('/portal/orders', { method: 'POST', body: JSON.stringify(input) });
}

export function updateOrder(orderId: string, input: UpdateOrderInput): Promise<Order> {
  return apiRequest<Order>(`/portal/orders/${orderId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function submitOrder(orderId: string): Promise<Order> {
  return apiRequest<Order>(`/portal/orders/${orderId}/submit`, { method: 'POST' });
}

export function cancelOrder(orderId: string, reason: string): Promise<Order> {
  return apiRequest<Order>(`/portal/orders/${orderId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function repeatOrder(
  siteId: string,
  serviceDate: string,
  operatingUnitId?: string,
): Promise<RepeatOrderResult> {
  return apiRequest<RepeatOrderResult>('/portal/orders/repeat', {
    method: 'POST',
    body: JSON.stringify({
      siteId,
      serviceDate,
      ...(operatingUnitId === undefined ? {} : { operatingUnitId }),
    }),
  });
}

export function createChangeRequest(
  orderId: string,
  items: OrderLineInput[],
): Promise<OrderChangeRequest> {
  return apiRequest<OrderChangeRequest>(`/portal/orders/${orderId}/change-requests`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

export function listAvailableMenus(
  serviceDate: string,
  operatingUnitId?: string,
): Promise<AvailableMenu[]> {
  const unit =
    operatingUnitId === undefined ? '' : `&operatingUnitId=${encodeURIComponent(operatingUnitId)}`;

  return apiRequest<AvailableMenu[]>(
    `/portal/menus/calendar?serviceDate=${encodeURIComponent(serviceDate)}${unit}`,
  );
}
